import { useEffect, useRef } from 'react'
import {
  autoUpdate,
  flip,
  offset,
  size,
  useFloating,
  useInteractions,
} from '@floating-ui/react'

import { FLOATING_ELEMENT_PLACEMENT } from '../../../../constants/common'
import { PdfViewerEvent } from '../../../PdfViewer'
import {
  getDocFlowWidgetActiveAreaValue,
  isDocFlowMappingAreaVisible,
  isDocFlowMappingSplittingAreaVisible,
  isSelectBoxAreaDisabled,
  preventOverflow,
} from '../../DocFlowMapping'
import { useDocFlowContext } from '../useDocFlowContext'

import {
  DOC_FLOW_SELECT_AREA_INFO_FALLBACK_PLACEMENTS,
  DOC_FLOW_SELECT_AREA_INFO_OFFSET,
} from './useDocFlowPdfSelectArea.constants'
import { setDocFlowSelectAreaInfoMaxHeight } from './useDocFlowPdfSelectArea.utils'

export function useDocFlowPdfSelectArea({
  activeWidget,
  handleToggleOpenSelectAreaInfo,
  hideSelectArea,
  initSelectBoxStyle,
  isChanged,
  isInitSelectArea,
  isOpenValueInfo,
  isPdfFinishedRendering,
  pageNumber,
  pdfPageCoordinates,
  pdfPageRef,
  pdfSelectAreaInfoValue,
  pdfViewerInstance,
  processData,
  selectedBox,
  showSelectArea,
}) {
  const { phaseDisabled } = useDocFlowContext()

  const pdfSelectAreaInfoValueRef = useRef(pdfSelectAreaInfoValue)
  const isChangedRef = useRef(isChanged)

  useEffect(() => {
    pdfSelectAreaInfoValueRef.current = pdfSelectAreaInfoValue
  }, [pdfSelectAreaInfoValue])

  useEffect(() => {
    isChangedRef.current = isChanged
  }, [isChanged])

  // eslint-disable-next-line react-hooks/refs
  const pdfPageElement = pdfPageRef.current

  const { floatingStyles, refs } = useFloating({
    middleware: [
      offset(DOC_FLOW_SELECT_AREA_INFO_OFFSET),
      // `size` before `flip`: the info shrinks into the room under the box
      // instead of flipping over it
      size({
        apply: setDocFlowSelectAreaInfoMaxHeight,
        boundary: pdfPageElement,
      }),
      flip({
        boundary: pdfPageElement,
        fallbackPlacements: DOC_FLOW_SELECT_AREA_INFO_FALLBACK_PLACEMENTS,
      }),
      preventOverflow(pdfPageCoordinates),
    ],
    open: isOpenValueInfo,
    placement: FLOATING_ELEMENT_PLACEMENT.bottom,
    transform: false,
    whileElementsMounted: autoUpdate,
  })

  const { getFloatingProps, getReferenceProps } = useInteractions()

  function handlePageComplete({ pageNumber: nextPageNumber }) {
    if (nextPageNumber === 1) {
      if (isInitSelectArea.current) {
        showSelectArea()

        if (pdfSelectAreaInfoValueRef.current) {
          handleToggleOpenSelectAreaInfo(true)
        }
      }
    } else {
      hideSelectArea()
      handleToggleOpenSelectAreaInfo(false)
    }
  }

  function handleToggleSelectArea(initFunction, type) {
    // when widget is changed and select area was initiated
    if (
      (isInitSelectArea.current && !selectedBox) ||
      !isInitSelectArea.current
    ) {
      initFunction(type)

      if (pageNumber > 1) {
        hideSelectArea()
        handleToggleOpenSelectAreaInfo(false)
      }
    }
  }

  useEffect(() => {
    if (pdfViewerInstance) {
      const abortController = new AbortController()

      pdfViewerInstance.eventBus.on(
        PdfViewerEvent.PageChanging,
        handlePageComplete,
        { signal: abortController.signal }
      )

      return () => {
        abortController.abort()
      }
    }
  }, [pdfViewerInstance])

  useEffect(() => {
    if (pdfViewerInstance && isPdfFinishedRendering) {
      const isSplittingAreaVisible = isDocFlowMappingSplittingAreaVisible({
        activeWidget,
        processData,
      })

      const isMappingAreaVisible = isDocFlowMappingAreaVisible({
        activeWidget,
        processData,
      })

      if (isSplittingAreaVisible || isMappingAreaVisible) {
        handleToggleSelectArea(
          initSelectBoxStyle,
          getDocFlowWidgetActiveAreaValue({
            isMappingAreaVisible,
            isSplittingAreaVisible,
            processData,
          })
        )
      }
    }
  }, [activeWidget, isPdfFinishedRendering, processData])

  useEffect(() => {
    if (!isChanged) {
      handleToggleOpenSelectAreaInfo(false)
    }
  }, [isChanged])

  return {
    handleInitSelectArea: initSelectBoxStyle,
    handleToggleSelectArea,
    selectBoxAreaDisabled:
      isSelectBoxAreaDisabled({ activeWidget, processData }) || phaseDisabled,
    selectBoxAreaFloatingProps: {
      floatingStyles,
      getFloatingProps,
      getReferenceProps,
      refs,
    },
  }
}
